<template>
  <el-dialog
    v-model="visible"
    :show-close="false"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    width="580px"
    modal-class="patient-tag-list-modal"
    append-to-body
    destroy-on-close
    @close="handleClose"
    @opened="handleOpened"
    @keydown="handleDialogKeydown"
  >
    <!-- Custom Header -->
    <template #header>
      <div
        class="flex justify-between items-center px-6 py-4 bg-[#004879] text-white rounded-t-[10px]"
      >
        <div class="flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.67 13.13C18.04 14.06 19 15.32 19 17V20H23V17C23 14.82 19.43 13.53 16.67 13.13Z" fill="white"/>
            <path d="M15 12C17.21 12 19 10.21 19 8C19 5.79 17.21 4 15 4C14.53 4 14.09 4.1 13.67 4.24C14.5 5.27 15 6.58 15 8C15 9.42 14.5 10.73 13.67 11.76C14.09 11.9 14.53 12 15 12Z" fill="white"/>
            <path d="M9 12C11.21 12 13 10.21 13 8C13 5.79 11.21 4 9 4C6.79 4 5 5.79 5 8C5 10.21 6.79 12 9 12ZM9 6C10.1 6 11 6.9 11 8C11 9.1 10.1 10 9 10C7.9 10 7 9.1 7 8C7 6.9 7.9 6 9 6Z" fill="white"/>
            <path d="M9 14C6.33 14 1 15.34 1 18V20H17V18C17 15.34 11.67 14 9 14ZM15 18H3V18.01C3.2 17.29 6.3 16 9 16C11.7 16 14.8 17.29 15 18Z" fill="white"/>
          </svg>
          <span class="font-semibold text-[20px] leading-normal text-white">
            Patient under tag "{{ tagName }}"
          </span>
        </div>
        <button
          ref="closeBtn"
          class="bg-transparent border-none p-0 cursor-pointer w-7 h-7 flex items-center justify-center"
          aria-label="Close modal"
          @click="handleClose"
        >
          <img src="@/assets/MyPatient/close.svg" alt="Close" class="w-full h-full" />
        </button>
      </div>
    </template>

    <!-- Content -->
    <div class="modal-body">
      <!-- Count line -->
      <div v-if="!isLoading && !hasError" class="count-line">
        There {{ total === 1 ? 'is' : 'are' }} {{ total }} patient{{ total !== 1 ? 's' : '' }} under the tag "{{ tagName }}".
      </div>

      <!-- Loading skeleton -->
      <div v-if="isLoading" class="skeleton-container">
        <div v-for="i in 5" :key="i" class="skeleton-row">
          <div class="skeleton-avatar"></div>
          <div class="skeleton-text">
            <div class="skeleton-line w-[140px]"></div>
            <div class="skeleton-line w-[80px] mt-1"></div>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="hasError" class="error-state">
        <p>Failed to load patients. Please try again.</p>
        <button class="retry-btn" @click="fetchPage(currentPage)">Retry</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="patients.length === 0" class="empty-state">
        <p>No patients found under the tag "{{ tagName }}".</p>
      </div>

      <!-- Patient list -->
      <div v-else class="patient-list-wrapper">
        <div
          ref="listContainer"
          class="patient-list"
          role="listbox"
          :aria-label="`Patients under tag ${tagName}`"
        >
          <div
            v-for="(patient, index) in patients"
            :key="patient.patientId"
            class="patient-row"
            :class="{
              'patient-row--focused': focusedIndex === index,
            }"
            role="option"
            :aria-selected="focusedIndex === index"
            :tabindex="focusedIndex === index ? 0 : -1"
            @mouseenter="focusedIndex = index"
            @mouseleave="focusedIndex = -1"
            @click="handleGoToChat(patient)"
            @keydown.enter.prevent="handleGoToChat(patient)"
          >
            <!-- Avatar initials -->
            <div class="patient-avatar">
              <span class="avatar-initials">{{ getInitials(patient.displayName) }}</span>
            </div>

            <!-- Patient info -->
            <div class="patient-info">
              <div class="patient-details">
                <span class="patient-name">{{ patient.displayName }}</span>
                <template v-if="patient.gender">
                  <span class="separator">|</span>
                  <span class="patient-meta">{{ patient.gender }}</span>
                </template>
                <span class="separator">|</span>
                <span class="patient-meta">DOB: {{ formatDob(patient.dob) }}</span>
              </div>
            </div>

            <!-- Chat icon / Go to chat -->
            <div
              class="chat-action"
              @click.stop="handleGoToChat(patient)"
            >
              <svg
                v-if="focusedIndex !== index"
                class="chat-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path d="M11.25 3V8.25H3.8775L3 9.1275V3H11.25ZM12 1.5H2.25C1.8375 1.5 1.5 1.8375 1.5 2.25V12.75L4.5 9.75H12C12.4125 9.75 12.75 9.4125 12.75 9V2.25C12.75 1.8375 12.4125 1.5 12 1.5ZM15.75 4.5H14.25V11.25H4.5V12.75C4.5 13.1625 4.8375 13.5 5.25 13.5H13.5L16.5 16.5V5.25C16.5 4.8375 16.1625 4.5 15.75 4.5Z" fill="#374957"/>
              </svg>
              <span v-else class="go-to-chat-btn">
                <svg
                  class="chat-icon-inline"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path d="M11.25 3V8.25H3.8775L3 9.1275V3H11.25ZM12 1.5H2.25C1.8375 1.5 1.5 1.8375 1.5 2.25V12.75L4.5 9.75H12C12.4125 9.75 12.75 9.4125 12.75 9V2.25C12.75 1.8375 12.4125 1.5 12 1.5ZM15.75 4.5H14.25V11.25H4.5V12.75C4.5 13.1625 4.8375 13.5 5.25 13.5H13.5L16.5 16.5V5.25C16.5 4.8375 16.1625 4.5 15.75 4.5Z" fill="#004879"/>
                </svg>
                Go to chat
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination (always visible when data loaded) -->
      <div v-if="!isLoading && !hasError && total > 0" class="tag-pagination">
        <span class="tag-pagination__info">
          {{ startItem }} to {{ endItem }} of {{ total }} Patients
        </span>
        <div class="tag-pagination__controls">
          <button
            class="tag-pagination__nav-btn"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          >
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
              <path d="M7.41 1.41L6 0L0 6L6 12L7.41 10.59L2.83 6L7.41 1.41Z" fill="#004879"/>
            </svg>
          </button>
          <div class="tag-pagination__page-numbers">
            <button
              v-for="p in visiblePages"
              :key="p"
              :class="['tag-pagination__page-num', { 'tag-pagination__page-num--active': p === currentPage, 'tag-pagination__page-num--dots': p === '...' }]"
              :disabled="p === '...'"
              @click="typeof p === 'number' && goToPage(p)"
            >{{ p }}</button>
          </div>
          <button
            class="tag-pagination__nav-btn"
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
          >
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
              <path d="M0.59 10.59L2 12L8 6L2 0L0.59 1.41L5.17 6L0.59 10.59Z" fill="#004879"/>
            </svg>
          </button>
        </div>
        <div class="tag-pagination__per-page">
          <select :value="perPage" @change="changePerPage(+$event.target.value)">
            <option :value="5">5 per page</option>
            <option :value="10">10 per page</option>
            <option :value="20">20 per page</option>
          </select>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { getTagPatientsApi } from '@/api/patientTags';

export default {
  name: 'PatientTagListModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    tagId: {
      type: [Number, String],
      default: null,
    },
    tagName: {
      type: String,
      required: true,
    },
  },
  emits: ['update:modelValue', 'go-to-chat'],

  data() {
    return {
      patients: [],
      total: 0,
      currentPage: 1,
      perPage: 5,
      // cursorMap: { 1: null (first page), 2: cursor_for_page_2, ... }
      cursorMap: { 1: null },
      isLoading: false,
      hasError: false,
      focusedIndex: -1,
      triggerElement: null,
    };
  },

  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    totalPages() {
      return Math.ceil(this.total / this.perPage) || 1;
    },
    startItem() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.perPage, this.total);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const pages = [];
      pages.push(1);
      if (current > 3) {
        pages.push('...');
      }
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (current < total - 2) {
        pages.push('...');
      }
      pages.push(total);
      return pages;
    },
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.triggerElement = document.activeElement;
        this.resetState();
        this.fetchPage(1);
      }
    },
  },

  methods: {
    resetState() {
      this.patients = [];
      this.total = 0;
      this.currentPage = 1;
      this.cursorMap = { 1: null };
      this.isLoading = false;
      this.hasError = false;
      this.focusedIndex = -1;
    },

    changePerPage(val) {
      this.perPage = val;
      this.currentPage = 1;
      this.cursorMap = { 1: null };
      this.fetchPage(1);
    },

    async fetchPage(page) {
      this.isLoading = true;
      this.hasError = false;
      this.focusedIndex = -1;
      try {
        const cursor = this.cursorMap[page] || undefined;
        const response = await getTagPatientsApi(this.tagId, {
          limit: this.perPage,
          cursor,
        });
        this.patients = response.patients || [];
        this.total = response.total || 0;
        this.currentPage = page;

        // Store cursor for next page
        if (response.nextCursor) {
          this.cursorMap[page + 1] = response.nextCursor;
        }
      } catch (error) {
        console.error('Failed to fetch tag patients:', error);
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages || typeof page !== 'number') return;
      this.fetchPage(page);
    },

    handleOpened() {
      this.$nextTick(() => {
        if (this.patients.length > 0) {
          this.focusedIndex = 0;
          this.focusCurrentRow();
        } else {
          this.$refs.closeBtn?.focus();
        }
      });
    },

    handleClose() {
      this.visible = false;
      this.$nextTick(() => {
        this.triggerElement?.focus();
      });
    },

    handleDialogKeydown(e) {
      if (this.patients.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (this.focusedIndex < this.patients.length - 1) {
            this.focusedIndex++;
            this.focusCurrentRow();
            this.ensureRowVisible();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (this.focusedIndex > 0) {
            this.focusedIndex--;
            this.focusCurrentRow();
            this.ensureRowVisible();
          }
          break;
        case 'Enter':
          if (this.focusedIndex >= 0 && this.focusedIndex < this.patients.length) {
            e.preventDefault();
            this.handleGoToChat(this.patients[this.focusedIndex]);
          }
          break;
      }
    },

    focusCurrentRow() {
      this.$nextTick(() => {
        const container = this.$refs.listContainer;
        if (!container) return;
        const rows = container.querySelectorAll('.patient-row');
        rows[this.focusedIndex]?.focus();
      });
    },

    ensureRowVisible() {
      this.$nextTick(() => {
        const container = this.$refs.listContainer;
        if (!container) return;
        const rows = container.querySelectorAll('.patient-row');
        const row = rows[this.focusedIndex];
        if (row) {
          row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    },

    handleGoToChat(patient) {
      this.$emit('go-to-chat', { patientId: patient.patientId });
      this.handleClose();
    },

    getInitials(displayName) {
      if (!displayName) return '';
      const parts = displayName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return (parts[0]?.[0] || '').toUpperCase();
    },

    formatDob(dob) {
      if (!dob) return '';
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const parts = dob.split('-');
      if (parts.length !== 3) return dob;
      const [year, month, day] = parts;
      const monthIndex = parseInt(month, 10) - 1;
      if (monthIndex < 0 || monthIndex > 11) return dob;
      return `${months[monthIndex]}-${day}-${year}`;
    },
  },
};
</script>

<style scoped>
.modal-body {
  padding: 16px 32px 24px;
  background: #fff;
}

.count-line {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 28px;
  color: #374957;
  margin-bottom: 8px;
}

/* Skeleton loading */
.skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
}

.skeleton-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
}

.skeleton-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;
}

.skeleton-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.skeleton-line {
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Error state */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 16px;
  color: #666;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
}

.retry-btn {
  padding: 6px 16px;
  border: 1px solid #004879;
  border-radius: 4px;
  background: white;
  color: #004879;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}

.retry-btn:hover {
  background: rgba(0, 72, 121, 0.08);
}

/* Empty state */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: #666;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
}

/* Patient list wrapper */
.patient-list-wrapper {
  background: #fff;
  border-radius: 10px;
  padding: 8px 24px;
}

/* Patient list */
.patient-list {
  min-height: 200px;
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #c4c4c4 transparent;
}

.patient-list::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.patient-list::-webkit-scrollbar-thumb {
  background: #c4c4c4;
  border-radius: 2px;
}

/* Patient row */
.patient-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  border-left: 3px solid transparent;
}

.patient-row:hover,
.patient-row--focused {
  background: var(--cool-white, #F9FBFD);
  border-left: 3px solid var(--VibrantDarkBlue, #004879);
}

/* Avatar */
.patient-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e8e9ea;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-initials {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #979797;
  text-align: center;
}

/* Patient info */
.patient-info {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.patient-details {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.patient-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.separator {
  color: #c4c4c4;
  font-size: 12px;
  flex-shrink: 0;
}

.patient-meta {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  white-space: nowrap;
}

/* Chat action */
.chat-action {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
}

.chat-icon {
  width: 18px;
  height: 18px;
}

.go-to-chat-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #004879;
  white-space: nowrap;
  cursor: pointer;
}

.go-to-chat-btn:hover {
  text-decoration: underline;
}

.chat-icon-inline {
  width: 14px;
  height: 14px;
}

/* Pagination — matches MentionsTab style */
.tag-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px 4px;
  font-family: 'Inter', sans-serif;
}

.tag-pagination__info {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #000;
  white-space: nowrap;
}

.tag-pagination__controls {
  display: flex;
  align-items: center;
  gap: 24px;
}

.tag-pagination__nav-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #E4F3F5;
  border: none;
  border-radius: 21px;
  cursor: pointer;
  padding: 0;
}

.tag-pagination__nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tag-pagination__page-numbers {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tag-pagination__page-num {
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border: none;
  border-radius: 17px;
  background: transparent;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.4px;
  color: #333;
  cursor: pointer;
}

.tag-pagination__page-num--active {
  background: #004879;
  color: #fff;
}

.tag-pagination__page-num--dots {
  cursor: default;
}

.tag-pagination__per-page select {
  height: 32px;
  padding: 4px 32px 4px 12px;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #333;
  background: white;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='8' viewBox='0 0 14 8' fill='none'%3E%3Cpath d='M1.645 0L0 1.52227L7 8L14 1.52227L12.355 0L7 4.94467L1.645 0Z' fill='%23374957'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px 6px;
  cursor: pointer;
  appearance: none;
}
</style>

<style>
/* Global dialog overrides for this modal */
.patient-tag-list-modal .el-dialog {
  border-radius: 10px;
  overflow: hidden;
  padding: 0;
}

.patient-tag-list-modal .el-dialog__header {
  padding: 0;
  margin: 0;
}

.patient-tag-list-modal .el-dialog__body {
  padding: 0;
}
</style>
